import { useIntl } from 'react-intl'
import React, { useMemo, useCallback } from 'react'

import { LabelValuePair } from '@/types'
import MUIMenuItem from '@mui/material/MenuItem'
import ClearIcon from '@mui/icons-material/Clear'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputAdorment from '@mui/material/InputAdornment'
import MUIFormHelperText from '@mui/material/FormHelperText'
import CircularProgress from '@mui/material/CircularProgress'
import MUISelect, { SelectChangeEvent } from '@mui/material/Select'

import { toKebabCase } from '@/services/helpers'
import IconButton from '@/components/Button/IconButton'

export type SelectItem = LabelValuePair & {
	disabled?: boolean
}

/**
 * Base properties for both single and multiple select components.
 * @template TOption - The type of option objects. Defaults to SelectItem.
 */
export type SelectSharedProps<TOption = SelectItem> = {
	label?: string
	options: TOption[]
	onBlur?: () => void
	/** Custom function to extract the display label from an option object */
	getOptionLabel?: (option: TOption) => string
	/** Callback fired when the options dropdown is closed */
	onCloseOptions?: () => void
	/** Key name in the option object used for comparison (defaults to 'value') */
	valueKey?: keyof TOption
	/** Key name in the option object used for display (defaults to 'label') */
	labelKey?: keyof TOption
	size?: 'medium' | 'small'
	loading?: boolean
	required?: boolean
	disabled?: boolean
	error?: boolean
	helperText?: string
	/**
	 * If `true`, always reserves space for the helper text below the select field,
	 * even when no helper text is provided. This prevents layout shift when
	 * validation errors or helper messages appear dynamically.
	 * Useful for keeping the field height consistent in forms with dynamic validation.
	 * @default true
	 */
	preserveHelperSpace?: boolean
	/** Callback fired when scrolling near the end of the options list */
	onEndReached?: () => void
	/** How far from the end (in units of visible length of the list)
	 * the bottom edge of the list must be from the end of the content to trigger
	 * the onEndReached callback. Thus a value of 0.5 will trigger onEndReached
	 * when the end of the content is within half the visible length of the list. */
	onEndReachedThreshold?: number
	/** Whether to disable the clear button */
	disableClear?: boolean
}

export type SelectBaseProps<TOption = SelectItem> =
	SelectSharedProps<TOption> & {
		/**
		 * The maximum height (in pixels) of the dropdown menu.
		 * If the number of options exceeds this height, the menu will become scrollable.
		 * @default 400
		 */
		dropdownMaxHeight?: number
		showEmptyOption?: boolean
		emptyOptionText?: string
		/** Custom render function for option items in the dropdown */
		renderOption?: (option: TOption) => React.ReactNode
		onClear?: () => void
	}

/**
 * Props for single-selection select component.
 * @template TOption - The type of option objects. Defaults to SelectItem.
 */
type SingleSelectProps<TOption = SelectItem> =
	SelectBaseProps<TOption> & {
		multiple?: false
		value: string
		onOptionChange?: (value: string) => void
	}

/**
 * Props for multiple-selection select component.
 * @template TOption - The type of option objects. Defaults to SelectItem.
 */
type MultipleSelectProps<TOption = SelectItem> =
	SelectBaseProps<TOption> & {
		multiple: true
		value: string[]
		onOptionChange?: (value: string[]) => void
	}

export type SelectProps<TOption = SelectItem> =
	| SingleSelectProps<TOption>
	| MultipleSelectProps<TOption>

/**
 * A flexible select component supporting both single and multiple selection modes.
 * Built on top of Material-UI's Select component, it provides features like infinite scrolling,
 * loading states, custom rendering, and internationalization.
 *
 * @template TOption - The type of option objects. Must have properties that can be used as value and label keys.
 * @param props - The component props, either SingleSelectProps or MultipleSelectProps
 * @returns A React functional component rendering the select input
 *
 * @example
 * // Single selection select
 * <Select
 *   label="Select user"
 *   options={options}
 *   value={user}
 *   onOptionChange={setUser}
 * />
 *
 * @example
 * // Single selection with custom option type
 * <Select<CustomValueLabelPair>
 *   label="Select option"
 *   valueKey="id"
 *   labelKey="name"
 *   options={customOptions}
 *   value={customOption}
 *   onOptionChange={setCustomOption}
 * />
 *
 * @example
 * // Multiple selection
 * <Select
 *   multiple
 *   label="Select user multiple"
 *   options={options}
 *   value={selectedMultiple}
 *   onOptionChange={setSelectedMultiple}
 * />
 *
 * @example
 * // Infinite scrolling
 * <Select
 *   label="Select branch"
 *   value={branch}
 *   options={options}
 *   onOptionChange={setBranch}
 *   loading={isLoading}
 *   onEndReachedThreshold={2}
 *   onEndReached={() => console.log('End reached')}
 * />
 */
export default function Select<TOption = SelectItem>(
	props: SelectProps<TOption>
) {
	const {
		label,
		showEmptyOption,
		emptyOptionText,
		options,
		multiple,
		value,
		onOptionChange,
		onBlur,
		onCloseOptions,
		getOptionLabel,
		renderOption,
		onClear,
		disableClear,
		loading,
		size,
		helperText,
		preserveHelperSpace = true,
		valueKey,
		labelKey,
		required,
		disabled,
		error,
		onEndReached,
		onEndReachedThreshold = 1,
		dropdownMaxHeight = 400,
	} = props

	const id = label
	const labelId = `${toKebabCase(id)}-label`
	const valueOptionKey = (valueKey ?? 'value') as keyof TOption
	const labelOptionKey = (labelKey ?? 'label') as keyof TOption
	const showClearButton =
		!disableClear && (multiple ? !!value[0] : value)

	const { formatMessage } = useIntl()

	const emptyOptionLabel =
		emptyOptionText ??
		formatMessage({
			id: 'global.select_option',
		})

	const handleChangeOption = (
		event: SelectChangeEvent<typeof value>
	) => {
		const {
			target: { value },
		} = event

		if (multiple) {
			onOptionChange?.(value as string[])
			return
		}

		onOptionChange?.(value as string)
	}

	/**
	 * Handles scroll events on the options listbox to implement infinite scrolling.
	 * Calls onEndReached when the user scrolls near the end of the list.
	 * @param event - React UI scroll event from the listbox element.
	 */
	const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
		if (!options?.length || !onEndReached) return

		const listboxNode = event.currentTarget

		const heightToBottom =
			listboxNode.scrollHeight -
			listboxNode.scrollTop -
			listboxNode.clientHeight

		const screensToBottom = heightToBottom / listboxNode.clientHeight

		if (screensToBottom <= onEndReachedThreshold) onEndReached()
	}

	const renderLabel = useCallback(
		(option: TOption) => {
			if (getOptionLabel) return getOptionLabel(option)

			return String(option[labelOptionKey])
		},
		[getOptionLabel, labelOptionKey]
	)

	const handleClear = () => {
		if (!disableClear) {
			onClear?.()

			if (multiple) {
				onOptionChange?.([] as string[])
				return
			}

			onOptionChange?.('' as string)
		}
	}

	const EmptyMenuItem = useMemo(
		() => (
			<MUIMenuItem value="">
				<em>{emptyOptionLabel}</em>
			</MUIMenuItem>
		),
		[emptyOptionLabel]
	)

	const MenuItems = useMemo(() => {
		return options.map((option, index) => {
			if (renderOption) return renderOption(option)

			return (
				<MUIMenuItem
					key={`${option[valueOptionKey]}-menu-item-${index}`}
					value={String(option[valueOptionKey])}
					disabled={(option as SelectItem).disabled}
				>
					{renderLabel(option)}
				</MUIMenuItem>
			)
		})
	}, [options, renderOption, renderLabel, valueOptionKey])

	return (
		<MUIFormControl fullWidth>
			{label && (
				<MUIInputLabel id={id} error={error} required={required}>
					{label}
				</MUIInputLabel>
			)}
			<MUISelect
				id={id}
				labelId={labelId}
				multiple={multiple}
				label={label}
				value={value}
				size={size}
				onChange={handleChangeOption}
				onBlur={onBlur}
				onClose={onCloseOptions}
				required={required}
				disabled={disabled}
				error={error}
				endAdornment={
					showClearButton && (
						<MUIInputAdorment position="end" className="mr-4">
							<IconButton onClick={handleClear}>
								<ClearIcon fontSize="small" />
							</IconButton>
						</MUIInputAdorment>
					)
				}
				MenuProps={{
					PaperProps: {
						onScroll: handleScroll,
						style: {
							maxHeight: dropdownMaxHeight,
						},
					},
				}}
			>
				{showEmptyOption && EmptyMenuItem}
				{MenuItems}
				{loading && (
					<div className="flex justify-center py-2 mt-[-20px]">
						<CircularProgress size={24} />
					</div>
				)}
			</MUISelect>

			<MUIFormHelperText error={error}>
				{preserveHelperSpace ? (helperText ?? ' ') : helperText}
			</MUIFormHelperText>
		</MUIFormControl>
	)
}
